import apiUrl from './api';
import { getWithAuth, postWithAuth } from '@/lib/httpAdapter';
import { removeSearchToolTags } from '@/utils/searchToolCleaner';

export interface DocumentItem {
  Title: string;
  Uuid: string;
  Type: 'SECTION' | 'SUBSECTION';
  ParentUUID: string;
  GenerationStatus?:
    | 'PENDING'
    | 'CREATED'
    | 'IN_PROGRESS'
    | 'COMPLETED'
    | 'FAILED';
}

export interface DocumentStructure {
  Title: string;
  Uuid: string;
  Type: string;
  Items: DocumentItem[];
  ParentUUID: string;
  GenerationStatus?:
    | 'PENDING'
    | 'CREATED'
    | 'IN_PROGRESS'
    | 'COMPLETED'
    | 'FAILED';
  /**
   * Status of the newest non-terminal generation job for this repository, or
   * '' when none is running. Item statuses cannot answer "is generation still
   * running": while the backend generates the section structure, every item
   * the sidebar can see already reads COMPLETED. Absent until the backend
   * change ships, so treat undefined as "unknown", never as "finished".
   */
  GenerationJobStatus?: string;
}

export interface FetchDocumentsSidebarResponse {
  Documents: DocumentStructure[];
  Error: string | null;
}

export interface FetchDocumentsSidebarParams {
  VectorDBUrl: string;
}

export interface DocumentWithContent {
  Title: string;
  Content: string;
  Uuid: string;
  Type: 'SECTION' | 'SUBSECTION';
  ParentUUID: string;
  generation_status?:
    | 'PENDING'
    | 'CREATED'
    | 'IN_PROGRESS'
    | 'COMPLETED'
    | 'FAILED';
  ProgressPercentage?: number;
  CurrentStep?: string;
  EstimatedTimeRemaining?: number;
}

export interface FetchDocumentStructureResponse {
  Document: DocumentWithContent;
  Error: string | null;
}

const endpoint = apiUrl + 'fetchDocumentsSidebar/';
const structureEndpoint = apiUrl + 'fetchDocumentStructure';

/**
 * Fetches the document sidebar structure from the API
 */
export const fetchDocumentsSidebar = async (
  params: FetchDocumentsSidebarParams
): Promise<FetchDocumentsSidebarResponse> => {
  try {
    const response = await postWithAuth(endpoint, params);

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.Error || `Server responded with status: ${response.status}`
      );
    }

    return data;
  } catch (error) {
    console.error('Error fetching document sidebar:', error);
    return {
      Documents: [],
      Error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

/**
 * Fetches the content of a specific document/section by UUID
 */
export const fetchDocumentStructure = async (
  uuid: string
): Promise<FetchDocumentStructureResponse> => {
  try {
    const finalUrl = `${structureEndpoint}/${uuid}/`;

    const response = await getWithAuth(finalUrl, {
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'cors',
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.Error || `Server responded with status: ${response.status}`
      );
    }

    if (data.Document && data.Document.Content) {
      data.Document.Content = removeSearchToolTags(data.Document.Content);
    }

    return data;
  } catch (error) {
    console.error('Error fetching document structure:', error);
    return {
      Document: {
        Title: '',
        Content: '',
        Uuid: '',
        Type: 'SECTION',
        ParentUUID: '',
      },
      Error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};
